import React from 'react';
import './PracticeSelectionStyle.scss';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Radio,
  Slide,
  Typography,
  makeStyles,
  Theme,
  Button
} from '@material-ui/core';
import {TransitionProps} from '@material-ui/core/transitions/transition';
import {useTranslation} from 'react-i18next';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {Close} from '@material-ui/icons';
import HelperUtils from '@shared/utils/HelperUtils';
import ClinicGroupsIcon from '@components/system/MyIcons/ClinicGroupsIcon';
import {useSelector} from 'react-redux';

interface PracticeListItem {
  id: number;
  name: string;
  address: string;
  address2: string;
  zip: string | number;
  city: string;
  state: string;
  clinicLogoUrl: string | null;
  balance?: number;
}

interface PracticeSelectionProps {
  title: string;
  fullScreen?: boolean;
  open: boolean;
  practiceList: Array<PracticeListItem>;
  selectedPractice: PracticeListItem;
  guarantor: {
    firstName: string;
    middleName: string;
    lastName: string;
  };
  groupDetails?: {
    name: string;
    address: string;
    address2: string;
    zip: string | number;
    city: string;
    state: string;
  };
  onClick?(clinic: PracticeListItem): void;
  onClose?(): void;
  fromAppointmentPortal?: boolean;
}

const drawerStyles: any = makeStyles((theme: Theme) => ({
  drawer: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent'
    }
  },
  dialogBoxShadow: {
    boxShadow: '0px 0px 12px 0px rgb(0 0 0 / 25%)'
  }
}));

const PracticeSelection = (
  props: PracticeSelectionProps
): React.ReactElement => {
  const {t} = useTranslation();

  const {
    title,
    open,
    fullScreen,
    guarantor,
    practiceList,
    selectedPractice,
    groupDetails,
    onClick,
    onClose,
    fromAppointmentPortal
  } = props;

  const classes = drawerStyles();
  const statementsEnable: boolean = useSelector(
    (state: any) =>
      state.payments.clinicDetails?.clinic_group_details?.statements || false
  );
  const renderLogo = React.useCallback(() => {
    let logoUrl = null;
    if (
      practiceList &&
      Array.isArray(practiceList) &&
      practiceList.length > 0
    ) {
      const logo = practiceList.filter(l => {
        return l.clinicLogoUrl != null;
      });
      if (logo.length > 0) {
        logoUrl = logo[0].clinicLogoUrl;
      }
    }
    if (logoUrl) {
      return (
        <img className="practice-icon" alt={'practice-logo'} src={logoUrl} />
      );
    }
    return <ClinicGroupsIcon className="practice-icon" />;
  }, [practiceList]);

  const handleClick = React.useCallback(
    (clinic: PracticeListItem): void => {
      if (typeof onClick === 'function') {
        onClick(clinic);
      }
    },
    [onClick]
  );
  const handleClose = React.useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);
  return (
    <Dialog
      TransitionComponent={React.forwardRef(function Transition(
        props1: TransitionProps & {children?: React.ReactElement<any, any>},
        ref: React.Ref<unknown>
      ) {
        return <Slide direction="up" ref={ref} {...props1} />;
      })}
      open={open}
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth="md"
      className={['guarantor-practice-selection', classes.drawer].join(' ')}
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog"
      disableBackdropClick={true}
      PaperProps={{
        style: {
          boxShadow: '0px 0px 12px 0px rgb(0 0 0 / 25%)'
        }
      }}>
      <DialogTitle disableTypography>
        <div className="dialog-title-wrapper">
          <div className="dialog-group-wrapper">
            <div className="group-details-wrapper">
              {groupDetails ? (
                <div className="group-details">
                  <div className="logo">{renderLogo()}</div>
                  <div className="group-details-info">
                    <div className="title">{groupDetails.name || ''}</div>
                    <div className="address">
                      {HelperUtils.formatFullAddress({
                        line1: groupDetails.address,
                        line2: groupDetails.address2,
                        city: groupDetails.city,
                        state: groupDetails.state,
                        country: '',
                        zip: (groupDetails.zip || '').toString()
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div />
              )}
              <span className="practice-title">{title}</span>
            </div>
            {selectedPractice.id > 0 && (
              <Button
                variant="outlined"
                color="primary"
                className={'close-link'}
                endIcon={<Close />}
                onClick={() => {
                  handleClose();
                }}>
                {t('COMMON.CLOSE')}
              </Button>
            )}
          </div>
          <div className={'practice-subtitle'}>
            {/* Guarantor name view start */}
            <Typography className="guarantor-name" component={'div'}>
              {t('PAYMENTS_LOGIN.DASHBOARD.HELLO', {
                // name: apiResponseData?.guarantor_details?.name,
                name: HelperUtils.formFullName(
                  guarantor.firstName,
                  guarantor.middleName,
                  guarantor.lastName
                )
              })}
              ,
            </Typography>
            <Typography className="subtitle">
              {fromAppointmentPortal
                ? t('APPOINTMENTS_SCHEDULAR_PORTAL.APPOINTMENT_PORTAL_SUBTITLE')
                : t('PAYMENTS_LOGIN.PRACTICE_SELECTION.SUB_TITLE')}
            </Typography>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={'practice-wrapper'}>
          <div className={'practice-menu-wrapper'}>
            {practiceList?.map(clinic => (
              <MenuItem
                onClick={() => {
                  handleClick(clinic);
                }}
                key={clinic.id}
                value={clinic.id}>
                <Radio checked={selectedPractice.id === clinic.id} />
                <ListItemText
                  primary={clinic.name}
                  secondary={HelperUtils.formatFullAddress({
                    city: clinic.city,
                    line1: clinic.address,
                    line2: clinic.address2,
                    state: clinic.state,
                    zip: (clinic.zip || '').toString()
                  })}
                />
                {statementsEnable && (
                  <div className="balance">
                    {clinic.balance ? (
                      <CurrencyFormatter
                        value={clinic.balance}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        ignoreRound={true}
                        useSign={true}
                      />
                    ) : fromAppointmentPortal ? (
                      ''
                    ) : (
                      <div className="no-balance">
                        {t('PAYMENTS_LOGIN.PRACTICE_SELECTION.NO_BALANCE')}
                      </div>
                    )}
                  </div>
                )}
              </MenuItem>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PracticeSelection;
